'use client';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';
import type { PharmacyServicesSectionData } from '@/features/pharamacy/data/pharmacy.types';

const TREATMENTS_SECTION_ID = 'pharmacist_advices_treatment_section';

const scrollToTreatments = () => {
  const el = document.getElementById(TREATMENTS_SECTION_ID);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

type PharmacyServicesSectionProps = {
  data: PharmacyServicesSectionData;
  className?: string;
};

const markdownLinkPattern = /^\[([^\]]+)\]\(([^)]+)\)$/;

const getButtonContent = (content: string, link?: string) => {
  const markdownLinkMatch = content.match(markdownLinkPattern);

  return {
    href: markdownLinkMatch?.[2] ?? link ?? '#',
    label: markdownLinkMatch?.[1] ?? content,
  };
};

const getHeadingContent = (content: string) => {
  return content.trimStart().startsWith('#') ? content : `## ${content}`;
};

const isPharmacyBookingButton = (href: string, label: string) => {
  return (
    href === '/book-appointment' ||
    href === '/pharmacy/book' ||
    href.startsWith('/pharmacy/book?') ||
    label.trim().toLowerCase() === 'book an appointment'
  );
};

const PharmacyServicesSection = ({
  data,
  className = '',
}: PharmacyServicesSectionProps) => {
  return (
    <section
      data-testid="pharmacy-services-section"
      className={`content-wrapper py-8 md:py-10 ${className}`}
    >
      <div data-testid="pharmacy-services-heading">
        <RichText styles={data.heading.styles}>
          {getHeadingContent(data.heading.content)}
        </RichText>
      </div>

      <div
        data-testid="pharmacy-services-grid"
        className="grid grid-cols-1 gap-8 md:grid-cols-2"
      >
        {data.cards.map((card, index) => (
          <article
            key={`${card.title.content}-${index}`}
            data-testid="pharmacy-services-card"
            className="min-h-55 rounded-sm bg-white p-7 shadow-[0_12px_24px_rgba(0,0,0,0.18)] md:p-8"
          >
            <div
              data-testid="pharmacy-services-card-icon-wrapper"
              className="bg-theme-primary mb-6 flex size-14 items-center justify-center rounded-full"
            >
              <R2Image
                data-testid="pharmacy-services-card-icon"
                path={card.icon.urlDesktop || ''}
                alt={card.icon.alt || 'Service Icon'}
                width={32}
                height={32}
                className="size-8 object-contain"
              />
            </div>

            <div data-testid="pharmacy-services-card-title">
              <RichText styles={card.title.styles}>
                {card.title.content}
              </RichText>
            </div>

            <div data-testid="pharmacy-services-card-description">
              <RichText styles={card.description.styles}>
                {card.description.content}
              </RichText>
            </div>

            {card.serviceId !== undefined && (
              <button
                type="button"
                onClick={scrollToTreatments}
                className="bg-theme-action-primary mt-6 inline-flex min-h-12 min-w-48 items-center justify-center rounded-md px-6 text-center text-sm font-medium text-white transition-opacity hover:opacity-90"
              >
                Book an appointment
              </button>
            )}
          </article>
        ))}
      </div>

      {data.buttons.length > 0 && (
        <div
          data-testid="pharmacy-services-buttons-wrapper"
          className="mt-10 flex justify-center"
        >
          {data.buttons.map((button, index) => {
            const { href, label } = getButtonContent(
              button.content,
              button.link,
            );

            if (isPharmacyBookingButton(href, label)) {
              return (
                <PrimaryButton
                  key={`${button.content}-${index}`}
                  data={{ title: label }}
                  onClick={scrollToTreatments}
                />
              );
            }

            return (
              <PrimaryButton
                key={`${button.content}-${index}`}
                data={{ title: label, link: href }}
              />
            );
          })}
        </div>
      )}
    </section>
  );
};

export default PharmacyServicesSection;
